import { ActionContext, ActionTree } from "vuex";
import { IFinancialProfileState } from "@/core/interfaces/financialProfileState.interface";
import {
  ICoreErrorResponse,
  ICoreResponse,
} from "@/core/interfaces/coreResponse.interface";
import { StatusCodes } from "http-status-codes";
import FinancialProfileService from "@/modules/FinancialProfile/services/financialProfile.service";
import { LinkOrganizationDto } from "@/modules/FinancialProfile/dtos/linkOrganization.dto";
import { GetFinancialProfileDto } from "@/modules/FinancialProfile/dtos/financialProfile.dto";
import { INotification } from "@/core/interfaces/notification.interface";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import {
  NOTIFICATION_EVENTS,
  notificationBus,
} from "@/core/eventBusses/notification";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import { StoreState } from "@/store/type";

const actions: ActionTree<IFinancialProfileState, StoreState> = {
  async fetchFinancialProfiles(
    context: ActionContext<IFinancialProfileState, StoreState>,
    params: GetFinancialProfileDto,
  ) {
    try {
      context.commit("enableIsFetchingFinancialProfiles");
      const financialProfileService = new FinancialProfileService();
      const response: ICoreResponse =
        await financialProfileService.getAll(params);
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("saveFinancialProfiles", response.data);
        context.commit("saveIssuanceFinancialProfiles", response.data);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      context.commit("disableIsFetchingFinancialProfiles");
    }
  },

  async fetchFinancialProfile(
    context: ActionContext<IFinancialProfileState, StoreState>,
    { id, params }: { id: string; params: GetFinancialProfileDto },
  ) {
    try {
      context.commit("enableIsFetchingFinancialProfile");
      const financialProfileService = new FinancialProfileService();
      const response: ICoreResponse = await financialProfileService.getById(
        id,
        params,
      );
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("saveFinancialProfile", response.data);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      context.commit("disableIsFetchingFinancialProfile");
    }
  },

  async linkFinancialProfileWithOrganization(
    context: ActionContext<IFinancialProfileState, StoreState>,
    {
      body,
      params,
      failureCallback,
      successCallback,
      financialProfileId,
    }: {
      body: LinkOrganizationDto;
      params: GetFinancialProfileDto;
      failureCallback: () => void;
      successCallback: () => void;
      financialProfileId: string;
    },
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    const notificationData: INotification = {
      type: NOTIFICATION_TYPES.INFO,
      description: "",
    };
    try {
      context.commit("enableIsLinkingFinancialProfile");
      const financialProfileService = new FinancialProfileService();
      response = await financialProfileService.linkWithOrganization(
        body,
        financialProfileId,
        params,
      );
      if (response.success && response.status === StatusCodes.OK) {
        notificationData.type = NOTIFICATION_TYPES.SUCCESS;
        notificationData.description =
          NOTIFICATION_MESSAGES.LINKED_FINANCIAL_PROFILE_SUCCESS;

        successCallback();
        context.commit("appendFinancialProfileInOrganization", response.data);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      notificationData.type = NOTIFICATION_TYPES.ERROR;
      notificationData.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      failureCallback();
    } finally {
      notificationBus.emit(NOTIFICATION_EVENTS.TRIGGER, notificationData);
      context.commit("disableIsLinkingFinancialProfile");
    }
  },
  async unlinkFinancialProfile(
    context: ActionContext<IFinancialProfileState, StoreState>,
    { id, successCallback }: { id: string; successCallback: () => void },
  ): Promise<void> {
    const notificationData: INotification = {
      type: NOTIFICATION_TYPES.INFO,
      description: "",
    };
    try {
      context.commit("enableIsUnlinkingFinancialProfile");
      const financialProfileService = new FinancialProfileService();
      await financialProfileService.unlinkFinancialProfile(id);
      context.commit("removeFinancialProfileFromOrganization", id);
      successCallback();
      notificationData.type = NOTIFICATION_TYPES.SUCCESS;
      notificationData.description =
        NOTIFICATION_MESSAGES.UNLINKED_FINANCIAL_PROFILE_SUCCESS;
    } catch (error: unknown) {
      notificationData.type = NOTIFICATION_TYPES.ERROR;
      notificationData.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      notificationBus.emit(NOTIFICATION_EVENTS.TRIGGER, notificationData);
      context.commit("disableIsUnlinkingFinancialProfile");
    }
  },
};

export default actions;
